import {
  AmHolidayType,
  AmLocation,
  asDateString,
  Location,
  Locations,
  PreviousSearch,
  TravelDates,
  TravelType,
} from '../../common';
import { LocationItem } from './LocationModal/LocationResult/LocationResult';

export const locationToItem = ({ locationCode, cityName, title, countryCode, type }: Location): LocationItem => ({
  id: locationCode,
  title: cityName,
  subtitle: title,
  locationCode,
  countryCode,
  type,
});

export const amLocationToItem = (
  { destinationCode, city, countryCode }: AmLocation,
  holidayType: AmHolidayType
): LocationItem => ({
  id: destinationCode,
  title: city,
  subtitle: holidayType.label,
  countryCode,
});

export const amLocationToListItem = ({ destinationCode, city, country, countryCode }: AmLocation): LocationItem => ({
  id: destinationCode,
  title: city,
  subtitle: country,
  countryCode,
});

type SearchableLocation = { city: string; country: string };

/**
 * Amazing sorting algorithm to always find the location you wanted!
 * Uses Levenshtein distance but prioritizes case-insensitive partial matches.
 */
export const locationLevenshteinPartialMatchSort = (query: string) => (a: SearchableLocation, b: SearchableLocation) =>
  levenshteinDistance(query, a.city) +
  levenshteinDistance(query, a.country) -
  (levenshteinDistance(query, b.city) + levenshteinDistance(query, b.country)) -
  (partialMatch(a.city, query) ? 100 : 0) -
  (partialMatch(a.country, query) ? 100 : 0) +
  (partialMatch(b.country, query) ? 100 : 0) +
  (partialMatch(b.city, query) ? 100 : 0);

const partialMatch = (a: string, b: string): boolean => a.toLowerCase().includes(b.toLowerCase());

const levenshteinDistance = (a = '', b = ''): number => {
  const distanceMatrix = Array(b.length + 1)
    .fill(null)
    .map(() => Array(a.length + 1).fill(null));

  for (let i = 0; i <= a.length; i += 1) {
    distanceMatrix[0][i] = i;
  }

  for (let j = 0; j <= b.length; j += 1) {
    distanceMatrix[j][0] = j;
  }

  for (let j = 1; j <= b.length; j += 1) {
    for (let i = 1; i <= a.length; i += 1) {
      const indicator = a[i - 1] === b[j - 1] ? 0 : 1;
      distanceMatrix[j][i] = Math.min(
        distanceMatrix[j][i - 1] + 1, // deletion
        distanceMatrix[j - 1][i] + 1, // insertion
        distanceMatrix[j - 1][i - 1] + indicator // substitution
      );
    }
  }

  return distanceMatrix[b.length][a.length];
};

const DAY_IN_MS = 1000 * 60 * 60 * 24;

const creationIsBetween14Days = (search: PreviousSearch): boolean => {
  return Math.floor((Date.now() - search.creationDate) / DAY_IN_MS) < 14;
};

const departureIsNotInPast = (search: PreviousSearch): boolean => {
  return search.travelDates.departureDate >= asDateString(new Date());
};

export const searchIsValid = (search: PreviousSearch): boolean =>
  creationIsBetween14Days(search) && departureIsNotInPast(search);

export const getItineraryForLocations = (
  locations: Locations[],
  travelDates: TravelDates,
  travelType: TravelType
): {
  departureLocationCode: string;
  destinationLocationCode: string;
  departureDate: string;
}[] => {
  const itinerary = [
    {
      departureLocationCode: locations[0].origin.locationCode,
      destinationLocationCode: locations[0].destination.locationCode,
      departureDate: travelDates.departureDate,
    },
  ];

  if (travelDates.returnDate) {
    if (travelType === TravelType.MULTI_CITY && locations[1] && locations[1].origin && locations[1].destination) {
      itinerary.push({
        departureLocationCode: locations[1].origin.locationCode,
        destinationLocationCode: locations[1].destination.locationCode,
        departureDate: travelDates.returnDate,
      });
    } else {
      itinerary.push({
        departureLocationCode: locations[0].destination.locationCode,
        destinationLocationCode: locations[0].origin.locationCode,
        departureDate: travelDates.returnDate,
      });
    }
  }

  return itinerary;
};
